import React, { useMemo, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";

const LOOKUP_OPTIONS = [
  { v: "parent_sku", l: "Parent SKU" },
  { v: "i_sku", l: "I-SKU" },
];

const UE_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "mp_name", label: "MP Name" },
  { key: "product_cost_tag", label: "Cost Tag" },
  { key: "primary_sp", label: "Primary SP" },
  { key: "secondary_sp", label: "Secondary SP" },
  { key: "status", label: "Status" },
  { key: "status_updated_date", label: "Status Date" },
  { key: "target_date", label: "Target Date" },
];

const LISTING_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "mp_name", label: "MP Name" },
  { key: "listing_id", label: "Listing ID" },
  { key: "ean_number", label: "EAN" },
  { key: "product_id", label: "Product ID" },
  { key: "status", label: "Status" },
  { key: "status_update_date", label: "Status Date" },
  { key: "target_date", label: "Target Date" },
];

const STATUS_STYLES = {
  completed: { bg: "#dcfce7", color: "#166534" },
  approved: { bg: "#dbeafe", color: "#1d4ed8" },
  active: { bg: "#dcfce7", color: "#15803d" },
  pending: { bg: "#fef3c7", color: "#92400e" },
  in_progress: { bg: "#e0e7ff", color: "#3730a3" },
  not_started: { bg: "#f3f4f6", color: "#374151" },
  rejected: { bg: "#fee2e2", color: "#b91c1c" },
};

function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch (_e) {
    return {};
  }
}

function parsePayload(raw) {
  const root = raw && typeof raw === "object" && raw.data && typeof raw.data === "object" ? raw.data : raw;
  return {
    lookup: root?.lookup || {},
    npd: root?.npd || null,
    skuData: Array.isArray(root?.sku_data) ? root.sku_data : [],
  };
}

function toLabel(value) {
  return String(value || "").replace(/_/g, " ");
}

function fmtValue(key, value) {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return `${value.length} items`;
  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value);
  const lk = String(key || "").toLowerCase();
  if (lk.includes("date") || lk.endsWith("_at")) {
    const d = new Date(text);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  return text;
}

function statusStyle(value) {
  return STATUS_STYLES[String(value || "").toLowerCase()] || { bg: "#e2e8f0", color: "#334155" };
}

function StatusPill({ value }) {
  const style = statusStyle(value);
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, background: style.bg, color: style.color, fontWeight: 700, fontSize: 11 }}>
      {toLabel(value || "na")}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={s.statCard}>
      <div style={s.statValue}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

function KeyValueGrid({ title, items }) {
  if (!items?.length) return null;
  return (
    <div style={s.sectionCard}>
      {title ? <div style={s.sectionTitle}>{title}</div> : null}
      <div style={s.kvGrid}>
        {items.map((item) => (
          <div key={item.label} style={s.kvCell}>
            <div style={s.kvLabel}>{item.label}</div>
            <div style={s.kvValue}>{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimpleTable({ title, rows, columns }) {
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const computedColumns = useMemo(() => {
    if (columns?.length) return columns;
    const keySet = new Set();
    safeRows.forEach((row) => Object.keys(row || {}).forEach((k) => keySet.add(k)));
    return Array.from(keySet).map((k) => ({ key: k, label: toLabel(k) }));
  }, [columns, safeRows]);

  return (
    <div style={s.sectionCard}>
      <div style={s.sectionTitle}>{title}</div>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {computedColumns.map((col) => (
                <th key={col.key} style={s.th}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, computedColumns.length)} style={s.emptyCell}>No rows</td>
              </tr>
            ) : (
              safeRows.map((row, idx) => (
                <tr key={row?.id || idx} style={{ borderTop: "1px solid #edf2f7" }}>
                  {computedColumns.map((col) => {
                    const value = row?.[col.key];
                    return (
                      <td key={col.key} style={s.td}>
                        {col.key === "status" ? <StatusPill value={value} /> : fmtValue(col.key, value)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StageGrid({ stages }) {
  const entries = Object.entries(stages || {});
  if (!entries.length) return null;
  return (
    <div style={s.sectionCard}>
      <div style={s.sectionTitle}>NPD Stages</div>
      <div style={s.stageGrid}>
        {entries.map(([key, value]) => (
          <div key={key} style={s.stageCard}>
            <div style={s.stageName}>{toLabel(key)}</div>
            <div style={{ marginBottom: 6 }}><StatusPill value={value?.status} /></div>
            <div style={s.stageMeta}>Target: {fmtValue("target_date", value?.target_date)}</div>
            <div style={s.stageMeta}>Completion: {fmtValue("completion_date", value?.completion_date)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NpdSection({ npd }) {
  if (!npd) return null;

  return (
    <div style={s.block}>
      <div style={s.blockTitle}>NPD</div>
      <KeyValueGrid
        title="NPD Summary"
        items={[
          { label: "I-SKU", value: npd.i_sku || "-" },
          { label: "Product Name", value: npd.product_name || "-" },
          { label: "SKU Type", value: npd.i_sku_type || "-" },
          { label: "Category", value: npd.product_category || "-" },
          { label: "Target Cost", value: npd.target_cost ?? "-" },
          { label: "Expected SP", value: npd.expected_sp ?? "-" },
          { label: "HSN", value: npd.hsn || "-" },
          { label: "GST %", value: npd.gst_percent ?? "-" },
          { label: "Primary Channel", value: npd.primary_channel || "-" },
          { label: "Other Channels", value: npd.other_channels || "-" },
          { label: "Final Status", value: <StatusPill value={npd.final_npd_status} /> },
          { label: "Remarks", value: npd.remarks || "-" },
        ]}
      />
      <SimpleTable title="Bundle Data" rows={npd?.bundle_data?.rows || []} columns={(npd?.bundle_data?.columns || []).map((k) => ({ key: k, label: toLabel(k) }))} />
      <SimpleTable title="Variation Data" rows={npd?.variation_data?.rows || []} columns={(npd?.variation_data?.columns || []).map((k) => ({ key: k, label: toLabel(k) }))} />
      <StageGrid stages={npd.stages} />
    </div>
  );
}

function SkuCard({ sku, expanded, onToggle }) {
  const componentRows = Array.isArray(sku.components) ? sku.components : [];

  return (
    <div style={s.skuCard}>
      <div style={s.skuHead}>
        <div>
          <div style={s.skuTitle}>{sku.sku || "-"}</div>
          <div style={s.skuSubtitle}>
            Parent: <strong>{sku.parent_sku || "-"}</strong> | Type: {sku.sku_type || "-"} | Business: {sku.business_type || "-"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StatusPill value={sku.sku_status} />
          <button type="button" style={s.toggleBtn} onClick={onToggle}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={s.skuBody}>
          <KeyValueGrid
            title="SKU Core"
            items={[
              { label: "ID", value: sku.id ?? "-" },
              { label: "Reference Code", value: sku.sku_reference_code || "-" },
              { label: "Name", value: sku.sku_name || "-" },
              { label: "Category", value: sku.category || "-" },
              { label: "Sub Category", value: sku.sub_category || "-" },
              { label: "EAN/UPC", value: sku.ean_upc || "-" },
              { label: "HSN", value: sku.hsn_code || "-" },
              { label: "MRP", value: sku.mrp ?? "-" },
              { label: "SKU Cost", value: sku.sku_cost ?? "-" },
              { label: "SKU Tax", value: sku.sku_cost_tax ?? "-" },
              { label: "Tag", value: sku.sku_tag || "-" },
              { label: "Created", value: fmtValue("created_at", sku.created_at) },
            ]}
          />

          <div style={s.sectionCard}>
            <div style={s.sectionTitle}>Components</div>
            {componentRows.length === 0 ? (
              <div style={s.emptyText}>No components</div>
            ) : (
              <div style={s.componentWrap}>
                {componentRows.map((component, idx) => (
                  <span key={`${component.comp_sku || "sku"}-${idx}`} style={s.componentChip}>
                    {component.comp_sku || "-"} x {component.comp_qty ?? "-"}
                  </span>
                ))}
              </div>
            )}
          </div>

          <SimpleTable title="Unit Economics" rows={sku.unit_economics || []} columns={UE_COLUMNS} />
          <SimpleTable title="Listings" rows={sku.listings || []} columns={LISTING_COLUMNS} />
          <SimpleTable title="Catalogue Master" rows={sku.catalogue_master || []} />
        </div>
      )}
    </div>
  );
}

export default function IskuExplorer() {
  const [lookupType, setLookupType] = useState("parent_sku");
  const [lookupValue, setLookupValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [expandedSkus, setExpandedSkus] = useState({});

  const skuRows = useMemo(() => (Array.isArray(payload?.skuData) ? payload.skuData : []), [payload]);
  const unitEconomicsCount = useMemo(
    () => skuRows.reduce((sum, row) => sum + (Array.isArray(row?.unit_economics) ? row.unit_economics.length : 0), 0),
    [skuRows],
  );
  const listingsCount = useMemo(
    () => skuRows.reduce((sum, row) => sum + (Array.isArray(row?.listings) ? row.listings.length : 0), 0),
    [skuRows],
  );

  async function fetchDetails() {
    const value = String(lookupValue || "").trim();
    if (!value) {
      setError("Please enter a lookup value.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set(lookupType, value);
      const response = await fetch(`${BASE_URL}/isku/?${params.toString()}`, {
        headers: getAuthHeader(),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json?.message || json?.error || "Failed to fetch iSKU details");

      const parsed = parsePayload(json);
      setPayload(parsed);
      setExpandedSkus(
        Object.fromEntries((parsed.skuData || []).map((row) => [String(row.id || row.sku || Math.random()), true])),
      );
    } catch (e) {
      setPayload(null);
      setError(e.message || "Failed to fetch iSKU details");
    } finally {
      setLoading(false);
    }
  }

  function toggleSku(row) {
    const key = String(row.id || row.sku);
    setExpandedSkus((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>I-SKU Explorer</h1>
            <p style={s.subtitle}>View NPD + SKU master + unit economics + listings in one place.</p>
          </div>
        </div>

        <div style={s.searchCard}>
          <select value={lookupType} onChange={(e) => setLookupType(e.target.value)} style={s.input}>
            {LOOKUP_OPTIONS.map((opt) => <option key={opt.v} value={opt.v}>{opt.l}</option>)}
          </select>
          <input
            value={lookupValue}
            onChange={(e) => setLookupValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchDetails(); }}
            placeholder={lookupType === "parent_sku" ? "Enter parent SKU (e.g. test-13-04-26)" : "Enter I-SKU (e.g. NPD-1304261747-KA)"}
            style={{ ...s.input, flex: 1 }}
          />
          <button type="button" style={s.btn} disabled={loading} onClick={fetchDetails}>
            {loading ? "Loading..." : "Fetch"}
          </button>
        </div>

        {error ? <div style={s.error}>{error}</div> : null}

        {payload && (
          <>
            <div style={s.statGrid}>
              <StatCard label="Lookup Type" value={toLabel(payload.lookup?.lookup_type || lookupType)} />
              <StatCard label="Lookup Value" value={payload.lookup?.lookup_value || lookupValue} />
              <StatCard label="SKUs Found" value={skuRows.length} />
              <StatCard label="Unit Economics Rows" value={unitEconomicsCount} />
              <StatCard label="Listings Rows" value={listingsCount} />
            </div>

            <NpdSection npd={payload.npd} />

            <div style={s.block}>
              <div style={s.blockTitle}>SKUs</div>
              {skuRows.length === 0 ? (
                <div style={s.sectionCard}>
                  <div style={s.emptyText}>No SKU data found for this lookup.</div>
                </div>
              ) : (
                skuRows.map((row, idx) => {
                  const key = String(row.id || row.sku || idx);
                  return (
                    <SkuCard
                      key={key}
                      sku={row}
                      expanded={!!expandedSkus[key]}
                      onToggle={() => toggleSku(row)}
                    />
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    fontFamily: "'DM Sans', sans-serif",
  },
  container: {
    maxWidth: 1600,
    margin: "0 auto",
    padding: "24px 20px 36px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 14,
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 26,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 13,
  },
  searchCard: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  input: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    outline: "none",
    color: "#1e293b",
    background: "#fff",
  },
  btn: {
    border: "none",
    borderRadius: 8,
    background: "#111827",
    color: "#fff",
    padding: "8px 14px",
    cursor: "pointer",
    fontWeight: 600,
  },
  error: {
    color: "#b91c1c",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 12,
    fontSize: 13,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
    marginBottom: 14,
  },
  statCard: {
    border: "1px solid #dbeafe",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#eff6ff",
  },
  statValue: {
    color: "#0f172a",
    fontWeight: 700,
    fontSize: 16,
    marginBottom: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statLabel: {
    color: "#475569",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: 700,
  },
  block: {
    marginBottom: 16,
  },
  blockTitle: {
    margin: "0 0 8px",
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  sectionCard: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    background: "#fff",
    padding: 12,
    marginBottom: 10,
  },
  sectionTitle: {
    marginBottom: 10,
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 8,
  },
  kvCell: {
    border: "1px solid #f1f5f9",
    borderRadius: 8,
    padding: "8px 10px",
    background: "#fcfdff",
  },
  kvLabel: {
    color: "#64748b",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: 700,
    marginBottom: 3,
  },
  kvValue: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #f1f5f9",
    borderRadius: 8,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 740,
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#64748b",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 10px",
    fontSize: 12,
    color: "#1e293b",
    whiteSpace: "nowrap",
  },
  emptyCell: {
    textAlign: "center",
    color: "#64748b",
    padding: 14,
    fontSize: 13,
  },
  emptyText: {
    color: "#64748b",
    fontSize: 13,
    padding: "6px 2px",
  },
  stageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 8,
  },
  stageCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 10,
    background: "#fff",
  },
  stageName: {
    fontSize: 12,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 8,
    textTransform: "capitalize",
  },
  stageMeta: {
    fontSize: 11,
    color: "#475569",
    marginTop: 4,
  },
  skuCard: {
    border: "1.5px solid #dbeafe",
    borderRadius: 12,
    background: "#fff",
    marginBottom: 12,
    overflow: "hidden",
  },
  skuHead: {
    padding: "12px 14px",
    background: "linear-gradient(180deg,#f8fbff 0%,#ffffff 100%)",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  skuTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 3,
  },
  skuSubtitle: {
    fontSize: 12,
    color: "#475569",
  },
  toggleBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#1e293b",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 10px",
  },
  skuBody: {
    padding: 12,
  },
  componentWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  componentChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: "#ecfeff",
    color: "#0f766e",
    border: "1px solid #99f6e4",
  },
};
